import React from "react";
import { Autocomplete, TextField } from "@mui/material";
import type { Job } from "../../../../../../../models";
import { ImportTableColumn } from "../../../../../../../utilities/types";
import { getValidationErrorKey } from "../../../../../../../utilities/jobValidation";
import { columnDisplayNames } from "../../../../../../admin/ImportSettings";
import { ColumnContext } from "../ColumnContext";
import { useValidationErrors } from "../../../shared/hooks/ValidationErrorsContext";
import { useSelectColumnViewModel } from "./selectColumn.vm";

export interface SelectColumnMobileProps<T = string | number> {
	/** The job being edited */
	job: Job;
	/** Column context for updates and validation */
	columnContext: ColumnContext;
	/** The field name on the Job object */
	fieldName: keyof Job;
	/** The column identifier */
	columnId: ImportTableColumn;
	/** Dropdown options */
	options?: T[];
	/** Whether the field is required */
	required?: boolean;
	/** Whether the field is disabled */
	disabled?: boolean;
	/** Custom label override */
	label?: string;
	/** Allow free text input */
	freeSolo?: boolean;
	/** Whether the value should be treated as a number */
	isNumber?: boolean;
	/** Custom option label getter */
	getOptionLabel?: (option: T) => string;
	/** Custom equality checker */
	isOptionEqualToValue?: (option: T, value: T) => boolean;
	/** Custom value getter from job */
	getValue?: (job: Job) => T | null;
	/** Custom value setter on job */
	setValue?: (job: Job, value: T | null) => void;
}

/**
 * Mobile-friendly select/autocomplete component for ImportTable fields.
 * Simplified version of selectColumn that doesn't depend on MRT.
 */
export function SelectColumnMobile<T = string | number>({
	job,
	columnContext,
	fieldName,
	columnId,
	options = [],
	required = false,
	disabled = false,
	label,
	freeSolo = true,
	isNumber = false,
	getOptionLabel,
	isOptionEqualToValue,
	getValue,
	setValue,
}: SelectColumnMobileProps<T>): React.ReactElement {
	const vm = useSelectColumnViewModel<T>(columnContext, columnId);
	const validationErrors = useValidationErrors();
	const errorKey = getValidationErrorKey(columnId, job.id);

	// Get current value using viewmodel
	const currentValue = vm.getCurrentValue(job, fieldName, getValue);
	const displayValue = vm.getDisplayValue(currentValue, freeSolo);

	// Handle non-freeSolo selection change
	const handleChange = (_event: React.SyntheticEvent, newValue: T | string | null) => {
		if (disabled) return;
		vm.handleSelectChange(job.id, fieldName, newValue as T | null, undefined, { setValue });
	};

	// Handle freeSolo blur (includes dealId auto-update for relevant fields)
	const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
		if (disabled || !freeSolo) return;
		vm.handleSelectBlur(job.id, fieldName, event.target.value, undefined, { isNumber });
	};

	const defaultGetOptionLabel = (option: T | string): string => {
		if (option === null || option === undefined) return "";
		return String(option);
	};

	// Handle the case where freeSolo allows string input
	const optionLabelHandler = getOptionLabel
		? (option: T | string) => (typeof option === "string" ? option : getOptionLabel(option as T))
		: defaultGetOptionLabel;

	return (
		<Autocomplete
			size="small"
			freeSolo={freeSolo}
			disabled={disabled}
			fullWidth
			value={displayValue}
			options={options}
			getOptionLabel={optionLabelHandler}
			isOptionEqualToValue={isOptionEqualToValue}
			onChange={freeSolo ? undefined : handleChange}
			onBlur={freeSolo ? handleBlur : undefined}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label || columnDisplayNames[columnId]}
					variant="outlined"
					required={required}
					error={!!validationErrors?.[errorKey]}
					helperText={validationErrors?.[errorKey]}
				/>
			)}
		/>
	);
}
